export type TranslationKey =
  | "feedback"
  | "reportYourIssue"
  | "findingMatchingJobs"
  | "noSuitableJobsFound"
  | "noMatchingJobsMessage"
  | "close"
  | "starting"
  | "end"
  | "start"
  | "language"
  | "settings"
  | "done"
  | "offlineMessage"
  | "reportAnIssue"
  | "yourFeedback"
  | "describeIssue"
  | "describeIssuePlaceholder"
  | "emailOptional"
  | "emailFollowUp"
  | "experienceQuestion"
  | "veryPoor"
  | "excellent"
  | "usabilityQuestion"
  | "veryDifficult"
  | "veryEasy"
  | "recommendQuestion"
  | "notAtAllLikely"
  | "extremelyLikely"
  | "jobMatchQuestion"
  | "notAtAllSatisfied"
  | "extremelySatisfied"
  | "commentsQuestion"
  | "yourComments"
  | "thankYou"
  | "thankYouIssue"
  | "thankYouFeedback"
  | "cancel"
  | "submit"
  | "submitting"
  | "endConversationTitle"
  | "endConversationMessage"
  | "continue"
  | "fetchingJobs"
  | "fetchingJobsMessage"
  | "stillWorking"
  | "stillWorkingMessage"
  | "requestTimeout"
  | "requestTimeoutMessage"
  | "mayTakeTwoMinutes"
  | "userLoadingError"
  | "userLoadingErrorMessage"
  | "loadingUserProfile"
  | "profileDetails"
  | "collapse"
  | "viewAll";

type Translations = {
  [key in TranslationKey]: string;
};

const english: Translations = {
  feedback: "Feedback",
  reportYourIssue: "Report your issue...",
  findingMatchingJobs: "Finding matching jobs based on your profile...",
  noSuitableJobsFound: "No Suitable Jobs Found",
  noMatchingJobsMessage:
    "Based on the conversation, we couldn't find any matching job openings at the moment.",
  close: "Close",
  starting: "Starting...",
  end: "End",
  start: "Start",
  language: "Language",
  settings: "Settings",
  done: "Done",
  offlineMessage: "You are offline. Please check your internet connection.",
  reportAnIssue: "Report an Issue",
  yourFeedback: "Your Feedback",
  describeIssue: "Please describe the issue you're experiencing with Iris",
  describeIssuePlaceholder:
    "Please describe the issue you're experiencing with Iris...",
  emailOptional: "Email (Optional)",
  emailFollowUp: "We'll use this to follow up on your issue if needed",
  experienceQuestion: "How would you rate your overall experience with Iris?",
  veryPoor: "Very poor",
  excellent: "Excellent",
  usabilityQuestion: "How easy did you find it to use Iris?",
  veryDifficult: "Very difficult",
  veryEasy: "Very easy",
  recommendQuestion:
    "How likely are you to recommend Iris to a friend or colleague?",
  notAtAllLikely: "Not at all likely",
  extremelyLikely: "Extremely likely",
  jobMatchQuestion:
    "Were you satisfied with the job match results provided by Iris?",
  notAtAllSatisfied: "Not at all satisfied",
  extremelySatisfied: "Extremely satisfied",
  commentsQuestion: "Do you have any other comments or suggestions for us?",
  yourComments: "Your comments...",
  thankYou: "Thank You!",
  thankYouIssue: "Thank you for reporting this issue. Our team will review it.",
  thankYouFeedback:
    "Your feedback has been submitted successfully. We appreciate you taking the time to share your thoughts!",
  cancel: "Cancel",
  submit: "Submit",
  submitting: "Submitting...",
  endConversationTitle: "End Conversation?",
  endConversationMessage:
    "Are you sure you want to end the conversation? Your profile will be saved.",
  continue: "Continue",
  fetchingJobs: "Fetching Jobs",
  fetchingJobsMessage: "Please wait while I am fetching jobs for you...",
  stillWorking: "Still Working...",
  stillWorkingMessage:
    "The job fetching process is taking slightly longer than expected. Please continue to wait while I find the best matches for you.",
  requestTimeout: "Request Timeout",
  requestTimeoutMessage:
    "Something went wrong with the job fetching process and the request was cancelled. Please try again later.",
  mayTakeTwoMinutes: "This may take up to 2 minutes",
  userLoadingError: "User Loading Error",
  userLoadingErrorMessage:
    "Failed to load your profile data. Please try refreshing the page.",
  loadingUserProfile: "Loading user profile...",
  profileDetails: "Profile Details",
  collapse: "Collapse",
  viewAll: "View All",
};

const polish: Translations = {
  feedback: "Opinia",
  reportYourIssue: "Zgłoś problem...",
  findingMatchingJobs: "Szukamy ofert pracy dopasowanych do Twojego profilu...",
  noSuitableJobsFound: "Nie znaleziono odpowiednich ofert pracy",
  noMatchingJobsMessage:
    "Na podstawie rozmowy nie udało nam się znaleźć pasujących ofert pracy.",
  close: "Zamknij",
  starting: "Uruchamianie...",
  end: "Zakończ",
  start: "Rozpocznij",
  language: "Język",
  settings: "Ustawienia",
  done: "Gotowe",
  offlineMessage: "Jesteś offline. Sprawdź swoje połączenie z internetem.",
  reportAnIssue: "Zgłoś problem",
  yourFeedback: "Twoja opinia",
  describeIssue: "Opisz problem, który napotkałeś podczas korzystania z Iris",
  describeIssuePlaceholder:
    "Opisz problem, który napotkałeś podczas korzystania z Iris...",
  emailOptional: "Email (opcjonalnie)",
  emailFollowUp:
    "Użyjemy go, aby skontaktować się z Tobą w sprawie zgłoszonego problemu",
  experienceQuestion: "Jak oceniasz swoje ogólne doświadczenie z Iris?",
  veryPoor: "Bardzo słabe",
  excellent: "Doskonałe",
  usabilityQuestion: "Jak łatwe było dla Ciebie korzystanie z Iris?",
  veryDifficult: "Bardzo trudne",
  veryEasy: "Bardzo łatwe",
  recommendQuestion:
    "Jak prawdopodobne jest, że polecisz Iris znajomemu lub współpracownikowi?",
  notAtAllLikely: "Wcale nieprawdopodobne",
  extremelyLikely: "Bardzo prawdopodobne",
  jobMatchQuestion:
    "Czy byłeś zadowolony z wyników dopasowania ofert pracy przez Iris?",
  notAtAllSatisfied: "Wcale niezadowolony",
  extremelySatisfied: "Bardzo zadowolony",
  commentsQuestion: "Czy masz inne uwagi lub sugestie dla nas?",
  yourComments: "Twoje uwagi...",
  thankYou: "Dziękujemy!",
  thankYouIssue:
    "Dziękujemy za zgłoszenie problemu. Nasz zespół go przeanalizuje.",
  thankYouFeedback:
    "Twoja opinia została przesłana. Dziękujemy za poświęcony czas!",
  cancel: "Anuluj",
  submit: "Wyślij",
  submitting: "Wysyłanie...",
  endConversationTitle: "Zakończyć rozmowę?",
  endConversationMessage:
    "Czy na pewno chcesz zakończyć rozmowę? Twój profil zostanie zapisany.",
  continue: "Kontynuuj",
  fetchingJobs: "Pobieranie ofert pracy",
  fetchingJobsMessage: "Proszę czekać, pobieram dla Ciebie oferty pracy...",
  stillWorking: "Nadal pracuję...",
  stillWorkingMessage:
    "Proces pobierania ofert pracy trwa nieco dłużej niż oczekiwano. Proszę czekać, szukam dla Ciebie najlepszych dopasowań.",
  requestTimeout: "Przekroczono limit czasu",
  requestTimeoutMessage:
    "Coś poszło nie tak podczas pobierania ofert pracy i żądanie zostało anulowane. Spróbuj ponownie później.",
  mayTakeTwoMinutes: "To może potrwać do 2 minut",
  userLoadingError: "Błąd ładowania użytkownika",
  userLoadingErrorMessage:
    "Nie udało się załadować danych Twojego profilu. Spróbuj odświeżyć stronę.",
  loadingUserProfile: "Ładowanie profilu użytkownika...",
  profileDetails: "Szczegóły profilu",
  collapse: "Zwiń",
  viewAll: "Zobacz wszystko",
};

const flemish: Translations = {
  feedback: "Feedback",
  reportYourIssue: "Meld uw probleem...",
  findingMatchingJobs: "Zoeken naar vacatures die bij uw profiel passen...",
  noSuitableJobsFound: "Geen geschikte vacatures gevonden",
  noMatchingJobsMessage:
    "Op basis van het gesprek konden we momenteel geen passende vacatures vinden.",
  close: "Sluiten",
  starting: "Starten...",
  end: "Beëindigen",
  start: "Starten",
  language: "Taal",
  settings: "Instellingen",
  done: "Klaar",
  offlineMessage: "U bent offline. Controleer uw internetverbinding.",
  reportAnIssue: "Een probleem melden",
  yourFeedback: "Uw feedback",
  describeIssue: "Beschrijf het probleem dat u ondervindt met Iris",
  describeIssuePlaceholder:
    "Beschrijf het probleem dat u ondervindt met Iris...",
  emailOptional: "E-mail (optioneel)",
  emailFollowUp:
    "We gebruiken dit om contact met u op te nemen over uw probleem indien nodig",
  experienceQuestion: "Hoe beoordeelt u uw algemene ervaring met Iris?",
  veryPoor: "Zeer slecht",
  excellent: "Uitstekend",
  usabilityQuestion: "Hoe gemakkelijk vond u het om Iris te gebruiken?",
  veryDifficult: "Zeer moeilijk",
  veryEasy: "Zeer gemakkelijk",
  recommendQuestion:
    "Hoe waarschijnlijk is het dat u Iris zou aanbevelen aan een vriend of collega?",
  notAtAllLikely: "Helemaal niet waarschijnlijk",
  extremelyLikely: "Zeer waarschijnlijk",
  jobMatchQuestion:
    "Was u tevreden met de vacatureresultaten die Iris heeft geleverd?",
  notAtAllSatisfied: "Helemaal niet tevreden",
  extremelySatisfied: "Zeer tevreden",
  commentsQuestion: "Heeft u nog andere opmerkingen of suggesties voor ons?",
  yourComments: "Uw opmerkingen...",
  thankYou: "Bedankt!",
  thankYouIssue:
    "Bedankt voor het melden van dit probleem. Ons team zal het bekijken.",
  thankYouFeedback:
    "Uw feedback is succesvol verzonden. We waarderen het dat u de tijd heeft genomen om uw mening te delen!",
  cancel: "Annuleren",
  submit: "Verzenden",
  submitting: "Verzenden...",
  endConversationTitle: "Gesprek beëindigen?",
  endConversationMessage:
    "Weet u zeker dat u het gesprek wilt beëindigen? Uw profiel wordt opgeslagen.",
  continue: "Doorgaan",
  fetchingJobs: "Vacatures ophalen",
  fetchingJobsMessage: "Even geduld, ik haal vacatures voor u op...",
  stillWorking: "Nog bezig...",
  stillWorkingMessage:
    "Het ophalen van vacatures duurt iets langer dan verwacht. Blijf even wachten terwijl ik de beste matches voor u zoek.",
  requestTimeout: "Verzoek verlopen",
  requestTimeoutMessage:
    "Er is iets misgegaan bij het ophalen van vacatures en het verzoek is geannuleerd. Probeer het later opnieuw.",
  mayTakeTwoMinutes: "Dit kan tot 2 minuten duren",
  userLoadingError: "Fout bij laden gebruiker",
  userLoadingErrorMessage:
    "Uw profielgegevens konden niet worden geladen. Probeer de pagina te vernieuwen.",
  loadingUserProfile: "Gebruikersprofiel laden...",
  profileDetails: "Profielgegevens",
  collapse: "Inklappen",
  viewAll: "Alles bekijken",
};

const translations: Record<string, Translations> = {
  English: english,
  Polish: polish,
  Flemish: flemish,
};

export function t(key: TranslationKey, language: string): string {
  const lang = translations[language] || translations.English;
  return lang[key] || translations.English[key];
}

export default translations;
